import React, { Component } from "react";
import ISPConfirm from "./ispConfirm";
import "../Form/proposalForm.css";
import {
  isElementsStrategyName,
  shouldSuppressElementsPathStrategy,
} from "./elementsFeeUtils";
import {
  canonicalizeMmmsFundSelection,
  getMmmsFundBondType,
  isManagedMoneyMarketStrategyName,
} from "./mmmsSelectionUtils";
import {
  buildFeeUpdateLegacyFields,
  normalizeFeeUpdateAccounts,
} from "./feeUpdateAccountUtils";
import { normalizeRegistrationDetails } from "./ispBusinessRules";
import {
  isPcsRegistrationStrategy,
  supportsPcsRegistrationType,
} from "./pcsRegistrationUtils";
import {
  isStandardRegistrationStrategy,
  normalizeStandardRegistrationRows,
  supportsStandardRegistrationType,
} from "./standardRegistrationUtils";

export default class ISPDetailsModal extends Component {
  componentDidUpdate(prevProps) {
    if (this.props.show !== prevProps.show) {
      document.body.style.overflow = this.props.show ? "hidden" : "unset";
    }
  }

  componentWillUnmount() {
    document.body.style.overflow = "unset";
  }

  normalizeBoolean = (value) => {
    return value === true || value === "true";
  };

  formatClientName = (client = {}) => {
    if (client.clientName) return client.clientName;
    if (client.name) return client.name;

    return [client.firstName, client.middleName, client.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
  };

  normalizeISPState = (proposal, lists = {}) => {
    if (!proposal?.ispFormData) return null;

    try {
      const parsed = JSON.parse(proposal.ispFormData);
      const riskScores = parsed.riskScores || {};
      const strategy =
        parsed.feeContextStrategy || parsed.strategy || proposal.program || "";
      const suppressElementsPath = shouldSuppressElementsPathStrategy(strategy);
      const managementFees =
        parsed.managementFees && typeof parsed.managementFees === "object"
          ? parsed.managementFees
          : null;
      const isFusion = String(strategy).trim().toLowerCase().includes("fusion");
      const fusionRegistrationType = isFusion
        ? String(parsed.fusionRegistrationType || "").trim().toLowerCase() ===
          "multi"
          ? "Multi"
          : "Single"
        : "";
      const storedFusionRebalanceType =
        parsed.fusionRebalanceType || proposal.ispFusionRebalanceType || "";
      const fusionRebalanceType = !isFusion
        ? ""
        : fusionRegistrationType === "Multi"
          ? "Manual"
          : String(storedFusionRebalanceType).trim().toLowerCase() === "manual"
            ? "Manual"
            : "Automatic";
      const registrations =
        Array.isArray(parsed.registrations) && parsed.registrations.length > 0
          ? parsed.registrations
          : [
              {
                registration: "registration0",
                value: parsed.registration || proposal.REGISTRATION || "",
                percentage: "100",
              },
            ];
      const jointClients = Array.isArray(parsed.jointClients)
        ? parsed.jointClients.map((client, index) => ({
            ...client,
            clientName:
              this.formatClientName(client) || `Client ${index + 1}`,
          }))
        : [];
      const requestType = parsed.requestType || proposal.ispRequestType || "";
      const existingRequestAction =
        parsed.existingRequestAction || proposal.ispExistingRequestType || "";
      const isFeeUpdate =
        requestType === "Existing" && existingRequestAction === "Fee Update";
      const registrationTypes = lists?.registrationTypes || [];
      const feeUpdateAccounts = isFeeUpdate
        ? normalizeFeeUpdateAccounts({
            ...parsed,
            strategy,
            accountNumber:
              parsed.accountNumber || proposal.ispAccountNumber || "",
            registrations,
          }, {
            registrationTypes,
          })
        : [];
      const feeUpdateLegacyFields = isFeeUpdate
        ? buildFeeUpdateLegacyFields(feeUpdateAccounts)
        : null;
      const isPcsRegistrationTypeEligible =
        supportsPcsRegistrationType(requestType, existingRequestAction) &&
        (isFeeUpdate
          ? feeUpdateAccounts.length > 0 &&
            feeUpdateAccounts.every((account) =>
              isPcsRegistrationStrategy(
                account.strategy,
                lists?.proposalTypes || [],
              ),
            )
          : isPcsRegistrationStrategy(
              strategy,
              lists?.proposalTypes || [],
            ));
      const pcsRegistrationType = isPcsRegistrationTypeEligible
        ? String(parsed.pcsRegistrationType || "").trim().toLowerCase() ===
          "multi"
          ? "Multi"
          : "Single"
        : "";
      const standardStrategies = Array.from(
        new Set(
          feeUpdateAccounts
            .map((account) => String(account?.strategy || "").trim())
            .filter(Boolean),
        ),
      );
      const isStandardRegistrationTypeEligible =
        supportsStandardRegistrationType(requestType, existingRequestAction) &&
        (isFeeUpdate
          ? standardStrategies.length === 1 &&
            feeUpdateAccounts.every((account) =>
              isStandardRegistrationStrategy(
                account.strategy,
                lists?.proposalTypes || [],
              ),
            )
          : isStandardRegistrationStrategy(
              strategy,
              lists?.proposalTypes || [],
            ));
      const standardRegistrationType = isStandardRegistrationTypeEligible
        ? String(
            parsed.standardRegistrationType ||
              proposal.ispStandardRegistrationType ||
              "",
          ).trim().toLowerCase() === "multi"
          ? "Multi"
          : "Single"
        : "";
      const savedStandardRegistrations = Array.isArray(
        parsed.standardRegistrations,
      )
        ? parsed.standardRegistrations
        : [];
      const normalizedRegistrations = isFeeUpdate
        ? pcsRegistrationType === "Multi"
          ? feeUpdateLegacyFields.registrations.map((registration, index) => ({
              ...registration,
              accountOwner: feeUpdateAccounts[index]?.accountOwner || "",
            }))
          : standardRegistrationType === "Multi"
            ? normalizeStandardRegistrationRows(feeUpdateAccounts)
          : feeUpdateLegacyFields.registrations
        : standardRegistrationType === "Multi"
          ? normalizeStandardRegistrationRows(
              savedStandardRegistrations.length > 0
                ? savedStandardRegistrations
                : registrations,
            )
          : registrations;
      const registrationValue =
        !isFeeUpdate &&
        fusionRegistrationType !== "Multi" &&
        pcsRegistrationType !== "Multi" &&
        standardRegistrationType !== "Multi"
          ? normalizedRegistrations[0]?.value || ""
          : "";
      const registrationDetails = normalizeRegistrationDetails(
        {
          custodianFullName:
            parsed.custodianFullName ||
            proposal.ispCustodianFullName ||
            parsed.custodialAdultName ||
            proposal.ispCustodialAdultName ||
            "",
          relationshipToPrimary:
            parsed.relationshipToPrimary ||
            proposal.ispRelationshipToPrimary ||
            "",
          trustName: parsed.trustName || proposal.ispTrustName || "",
          businessName:
            parsed.businessName || proposal.ispBusinessName || "",
          businessEinLast4:
            parsed.businessEinLast4 || proposal.ispBusinessEinLast4 || "",
          decedentName:
            parsed.decedentName || proposal.ispDecedentName || "",
          decedentSsnLast4:
            parsed.decedentSsnLast4 || proposal.ispDecedentSsnLast4 || "",
          relationshipToExecutor:
            parsed.relationshipToExecutor ||
            proposal.ispRelationshipToExecutor ||
            "",
          soloKQuestionnaire: parsed.soloKQuestionnaire,
        },
        registrationValue,
        registrationTypes,
      );
      const isRiskToleranceUpdate =
        requestType === "Existing" &&
        existingRequestAction === "Risk Tolerance Update";
      const storedBondType = parsed.bondType || proposal.ispBondType || "";
      const isManagedMoneyMarket = isManagedMoneyMarketStrategyName(strategy);
      const rawBondTypePromptShown = Object.prototype.hasOwnProperty.call(
        parsed,
        "bondTypePromptShown",
      )
        ? parsed.bondTypePromptShown
        : proposal.ispBondTypePromptShown;
      const bondTypePromptShown =
        !isManagedMoneyMarket &&
        this.normalizeBoolean(rawBondTypePromptShown);
      const rawBondTypePreferenceProvided = Object.prototype.hasOwnProperty.call(
        parsed,
        "bondTypePreferenceProvided",
      )
        ? parsed.bondTypePreferenceProvided
        : proposal.ispBondTypePreferenceProvided;
      const bondTypePreferenceProvided =
        bondTypePromptShown &&
        isRiskToleranceUpdate &&
        this.normalizeBoolean(rawBondTypePreferenceProvided) &&
        ["Taxable", "Municipal"].includes(storedBondType);
      const mmmsFundSelection = isManagedMoneyMarket
        ? canonicalizeMmmsFundSelection(
            parsed.modelSelection ||
              riskScores.selectedPortfolio ||
              proposal["RISK TOLERANCE"],
            storedBondType,
          )
        : "";

      return {
        advisorName: parsed.advisorName || proposal["ADVISOR NAME"] || "",
        custodian: parsed.custodian || proposal.ispCustodian || "",
        strategy,
        hasPath: suppressElementsPath ? false : parsed.hasPath === true,
        hasInitialFeeStage:
          parsed.hasInitialFeeStage === true ||
          (isElementsStrategyName(strategy) && managementFees?.flatFee != null),
        bondType: isManagedMoneyMarket
          ? getMmmsFundBondType(mmmsFundSelection)
          : isRiskToleranceUpdate
          ? bondTypePreferenceProvided
            ? storedBondType
            : ""
          : storedBondType || "Taxable",
        bondTypePromptShown,
        bondTypePreferenceProvided,
        pathBondType:
          suppressElementsPath
            ? ""
            : parsed.pathBondType || proposal.ispPathBondType || "",
        pathStrategy:
          suppressElementsPath
            ? ""
            : parsed.pathStrategy || proposal.ispPathStrategy || "",
        pathIarFeeType:
          suppressElementsPath
            ? "Fixed Percentage"
            : parsed.pathIarFeeType ||
              proposal.ispPathIarFeeType ||
              "Fixed Percentage",
        pathIarFeeFlat:
          suppressElementsPath
            ? ""
            : parsed.pathIarFeeFlat || proposal.ispPathIarFeeFlat || "",
        pathIarFeeTiered:
          suppressElementsPath || !Array.isArray(parsed.pathIarFeeTiered)
            ? []
            : parsed.pathIarFeeTiered,
        registrations: normalizedRegistrations,
        feeUpdateAccounts,
        feeUpdateStrategyFeeBlocks:
          isFeeUpdate && Array.isArray(parsed.feeUpdateStrategyFeeBlocks)
            ? parsed.feeUpdateStrategyFeeBlocks
            : [],
        requestType,
        existingRequestAction,
        accountNumber: parsed.accountNumber || proposal.ispAccountNumber || "",
        accountNumbers: isFeeUpdate
          ? feeUpdateLegacyFields.accountNumbers
          : Array.isArray(parsed.accountNumbers)
            ? parsed.accountNumbers
            : [],
        householding: parsed.householding || proposal.ispHouseholding || "",
        householdRef: parsed.householdRef || proposal.ispHouseholdRef || "",
        householdReason:
          parsed.householdReason || proposal.ispHouseholdReason || "",
        initialInvestmentAmount:
          parsed.initialInvestmentAmount || proposal.VALUE || "",
        accountNetWorthPct:
          parsed.accountNetWorthPct || proposal.ispNetWorthPct || "",
        accountIncomeNeed: parsed.accountIncomeNeed || proposal.ispIncomeNeed || "",
        modelSelection:
          mmmsFundSelection || parsed.modelSelection || proposal.ispModelSelection || "",
        accountGoal: parsed.accountGoal || proposal.ispPrimaryGoal || "",
        accountGoalOther: parsed.accountGoalOther || proposal.ispGoalExplain || "",
        ...registrationDetails,
        custodialAdultName: registrationDetails.custodianFullName,
        minorName: parsed.minorName || proposal.ispMinorName || "",
        relationshipToCustodian:
          parsed.relationshipToCustodian ||
          proposal.ispRelationshipToCustodian ||
          "",
        sourceOfFunds: Array.isArray(parsed.sourceOfFunds)
          ? parsed.sourceOfFunds
          : [],
        jointClients,
        combinedScore:
          riskScores.combinedScore ?? proposal.ispCombinedScore ?? "",
        riskCategory: riskScores.riskCategory || proposal.ispRiskCategory || "",
        selectedPortfolio:
          mmmsFundSelection ||
          riskScores.selectedPortfolio ||
          proposal["RISK TOLERANCE"] ||
          "",
        portfolioOverride: isManagedMoneyMarket
          ? false
          : this.normalizeBoolean(
              riskScores.portfolioOverride ?? proposal.ispPortfolioOverride,
            ),
        iarFeeTier: parsed.iarFeeTier || proposal.ispIarFeeTier || "",
        iarFeeType: parsed.iarFeeType || proposal.ispIarFeeType || "",
        iarFeeFlat: parsed.iarFeeFlat || proposal.ispIarFeeFlat || "",
        iarFeeTiered: Array.isArray(parsed.iarFeeTiered)
          ? parsed.iarFeeTiered
          : [],
        iarFeeScheduleType:
          parsed.iarFeeScheduleType ||
          proposal.ispIarFeeScheduleType ||
          "",
        managementFees,
        selectedManagementFeeTierKey:
          parsed.managementFeeTierKey || parsed.iarFeeTier || "",
        fusionAllocations: Array.isArray(parsed.fusionAllocations)
          ? parsed.fusionAllocations
          : [],
        fusionRegistrationType,
        pcsRegistrationType,
        standardRegistrationType,
        fusionRebalanceType,
      };
    } catch (error) {
      console.error("Error parsing ispFormData for details modal:", error);
      return null;
    }
  };

  render() {
    const { show, onHide, proposal, lists } = this.props;

    if (!show || !proposal) return null;

    const normalizedState = this.normalizeISPState(proposal, lists || {});

    return (
      <>
        <div
          className={`modal-backdrop fade ${show ? "show" : ""}`}
          style={{ display: show ? "block" : "none" }}
          onClick={onHide}
        />

        <div
          className={`modal fade ${show ? "show" : ""}`}
          tabIndex="-1"
          role="dialog"
          style={{
            display: show ? "block" : "none",
            overflowY: "auto",
          }}
          onClick={onHide}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-scrollable"
            role="document"
            style={{
              maxHeight: "90vh",
              margin: "1.75rem auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{ maxHeight: "90vh" }}>
              <div className="modal-header">
                <h5 className="modal-title vicus-blue">ISP Details</h5>
                <button
                  type="button"
                  className="close"
                  onClick={onHide}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div
                className="modal-body"
                style={{ overflowY: "auto", maxHeight: "calc(90vh - 120px)" }}
              >
                {normalizedState ? (
                  <ISPConfirm
                    state={normalizedState}
                    lists={lists || {}}
                    isFormValid={true}
                    onSubmit={() => {}}
                    isSubmitting={false}
                    submitError=""
                    viewOnly={true}
                  />
                ) : (
                  <div className="alert alert-danger" role="alert">
                    Unable to load the saved ISP details for this submission.
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onHide}
                  style={{
                    paddingLeft: "30px",
                    paddingRight: "30px",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}
